import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Wordmark } from "@/components/Logo";

// Detects installability and offers an "Install app" action (Android/desktop
// via beforeinstallprompt; iOS Safari via Add-to-Home-Screen instructions).
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches
  );

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIOS = typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

  return { deferred, installed, isIOS, canInstall: !!deferred || (isIOS && !installed) };
}

export default function InstallButton({ className = "", label = "Install app" }) {
  const { deferred, installed, isIOS, canInstall } = useInstallPrompt();
  const [showIos, setShowIos] = useState(false);

  if (installed || !canInstall) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
    } else if (isIOS) {
      setShowIos(true);
    }
  };

  return (
    <>
      <button onClick={onClick} data-testid="install-app-btn"
        className={className || "flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1.5 rounded-sm hover:-translate-y-[1px] transition-transform"}>
        <Download className="w-3.5 h-3.5" /> {label}
      </button>

      {showIos && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIos(false)} data-testid="ios-install-sheet">
          <div className="bg-card border border-border rounded-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">Add to Home Screen</h3>
              <button onClick={() => setShowIos(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <ol className="text-sm text-muted-foreground space-y-3">
              <li className="flex items-center gap-2">1. Tap the <Share className="w-4 h-4 text-primary inline" /> Share button in Safari.</li>
              <li>2. Scroll down and tap <span className="text-foreground font-medium">"Add to Home Screen"</span>.</li>
              <li>3. Tap <span className="text-foreground font-medium">Add</span> — <Wordmark /> will appear like a native app.</li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
